import React, { useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { getIcon } from "@dishopsaas/design-system";
import _ from "lodash";
import { OrderItemImages } from "../../Order/components/OrderItemImages";
import { getStatus } from "../../../utils";
import getStyles from "./CurrentOrdersNotification.styles";
import { ICONS } from "../../../constants";
import { goToOrdersPath } from "./CurrentOrdersNotification.services";

type Props = {
    orders: Record<string, any>;
};

export const CurrentOrdersNotification: React.FC<Props> = ({ orders }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const styles = getStyles(theme);

    const ref = useRef<HTMLDivElement | null>(null);

    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);

    const startPointer = useRef({ x: 0, y: 0 });
    const startPos = useRef({ x: 0, y: 0 });

    const MOVE_THRESHOLD = 6;

    const activeOrders = Object.values(orders || {});
    const isEmpty = _.isEmpty(activeOrders);
    const multiple = activeOrders.length > 1;
    const firstOrder = activeOrders[0];

    if (isEmpty) return null;

    const onPointerDown = (e: React.PointerEvent) => {
        setDragging(true);

        startPointer.current = {
            x: e.clientX,
            y: e.clientY,
        };

        startPos.current = pos;

        ref.current?.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!dragging) return;

        const dx = e.clientX - startPointer.current.x;
        const dy = e.clientY - startPointer.current.y;

        setPos({
            x: startPos.current.x + dx,
            y: startPos.current.y + dy,
        });
    };

    const onPointerUp = (e: React.PointerEvent) => {
        const dx = e.clientX - startPointer.current.x;
        const dy = e.clientY - startPointer.current.y;

        const isClick =
            Math.abs(dx) < MOVE_THRESHOLD &&
            Math.abs(dy) < MOVE_THRESHOLD;

        setDragging(false);

        if (ref.current?.hasPointerCapture(e.pointerId)) {
            ref.current.releasePointerCapture(e.pointerId);
        }

        if (isClick) {
            goToPath();
        }
    };

    const goToPath = () => {
        goToOrdersPath({
            navigate,
            locationPath: location.pathname,
            orders: activeOrders,
            multiple,
        });
    };

    return (
        <Box
            ref={ref}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            sx={{
                ...styles.container,
                transform: `translate(${pos.x}px, ${pos.y}px)`,
                transition: dragging ? "none" : "transform 0.2s ease",
                cursor: "grab",
                userSelect: "none",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
            }}
        >
            <Box sx={{ gap: 2, width: "100%" }}>
                <Box sx={{ ...styles.header, pb: !multiple && 2 }}>
                    <Typography variant="h5" sx={styles.title}>
                        {multiple
                            ? `${activeOrders.length} commandes en cours`
                            : "Commande en cours"}
                    </Typography>

                    {!multiple && firstOrder?.commande && (
                        <Box sx={styles.imagesContainer}>
                            <OrderItemImages commande={firstOrder.commande} />
                        </Box>
                    )}
                </Box>

                {!multiple && firstOrder && (
                    <Typography variant="caption" sx={styles.status}>
                        {getStatus(firstOrder)}
                    </Typography>
                )}
            </Box>

            <Box sx={styles.link}>
                <Typography variant="subtitle1">
                    {multiple
                        ? "Suivre mes commandes"
                        : "Voir ma commande"}
                </Typography>
                {getIcon(ICONS.ChevronRight)}
            </Box>
        </Box>
    );
};